import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, ServiceBase } from '@unleashed/api/common';
import { APP_OPTIONS, AppOptions } from '@unleashed/common/config';
import { AuthorizationService } from '@unleashed/services/account';
import { filter, switchMap, take } from 'rxjs/operators';
import { CheckoutTokenRequest } from '@unleashed/models/booking/checkoutTokenRequest';
import { CheckoutToken } from '@unleashed/models/booking/checkoutToken';

export interface JustifiPaymentMethodResponse {
  signature?: string;
  customerId?: string;
  accountId?: string;
  card?: {
    id?: string;
    name?: string;
    lastFour?: string;
    brand?: string;
    digitalWallet?: string; // "apple_pay", "google_pay", null
    token?: string;
    expirationMonth?: string;
    year?: string;
    metadata?: unknown;
    createdAt?: string;
    updatedAt?: string;
    addressLine1Check?: string;
    addressPostalCodeCheck?: string;
    binDetails?: {
      type?: string; // "Credit", "Debit", "Prepaid", "Unknown"
      cardBrand?: string; // "Visa", "Mastercard", "American Express", "Discover"
      cardClass?: string; // "Consumer"
      country?: string; // "United States of America"
      issuer?: string; // Issuing bank
      fundingSource?: string; // "Charge", "Credit", "Debit", "Deferred Debit (Visa Only)", "Network Only", "Prepaid"
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class JustifiApiService extends ServiceBase {

  subnav = 'invoices';  // backend: Controllers\TokenController.cs

  constructor(@Inject(APP_OPTIONS) appOptions: AppOptions, private apiService: ApiService, private auth: AuthorizationService) {
    super(appOptions.unleashedApiUrl);
  }

  // this one is deprecated, and can be deleted later
  getToken(brandSlug: string, locationSlug: string): Observable<string> {
    return this.auth.getAccessToken().pipe(
      // [STORE-474] accessToken is a BehaviorSubject seeded with '', so a bare subscription replays the
      // empty seed and fires the request with an empty Bearer (401) when it beats the freshly-registered
      // guest's token. Wait for the first non-empty token, then fire once.
      filter(token => !!token),
      take(1),
      switchMap(token =>
        this.apiService.post<string>(this.buildUrl(`tokens`) + `/${brandSlug}/${locationSlug}`, null,
          {headers: {Authorization: `Bearer ${token}`}}))
    );
  }

  getCheckoutToken(brandSlug: string, locationSlug: string, checkoutTokenRequest: CheckoutTokenRequest): Observable<CheckoutToken> {
    return this.auth.getAccessToken().pipe(
      // [STORE-474] Wait for a non-empty token before firing; see getToken above.
      filter(token => !!token),
      take(1),
      switchMap(token =>
        this.apiService.post<CheckoutToken, CheckoutTokenRequest>(this.buildUrl(`tokens`) + `/${brandSlug}/${locationSlug}/checkout`,
          checkoutTokenRequest,
          {headers: {Authorization: `Bearer ${token}`}}))
    );
  }

  updateCheckout(brandSlug: string, locationSlug: string, checkoutId: string, checkoutTokenRequest: CheckoutTokenRequest):
    Observable<boolean> {
    return this.auth.getAccessToken().pipe(
      // [STORE-474] Wait for a non-empty token before firing; see getToken above.
      filter(token => !!token),
      take(1),
      switchMap(token =>
        this.apiService.post<boolean, CheckoutTokenRequest>(
          this.buildUrl(`tokens`) + `/${brandSlug}/${locationSlug}/updateCheckout/${checkoutId}`,
          checkoutTokenRequest,
          {headers: {Authorization: `Bearer ${token}`}}))
    );
  }

  // No auth needed: SelfServiceController is [AllowAnonymous]
  deleteCheckout(checkoutId: string): Observable<boolean> {
    return this.apiService.post<boolean>(
      `${this.baseUrl}/accounts/selfservice/deleteCheckout/${checkoutId}`,
      null);
  }

  // No auth needed: token is a JustiFi payment method token, not account-sensitive data
  getPaymentMethod(paymentToken: string, countryId: string): Observable<JustifiPaymentMethodResponse> {
    return this.apiService.get<JustifiPaymentMethodResponse>(
      `${this.baseUrl}/accounts/selfservice/payment-methods/${paymentToken}`,
      {params: {countryId}});
  }
}
